import { useMutation, useQueryClient } from '@tanstack/react-query';
import { actOnPcmpProposalRequest, actOnPcmpRequests } from '../api/pcmpRequested.api';
import { describePcmpActOutcome } from '../constants/pcmpRequested.constants';
import type {
  PcmpRequestActed,
  PcmpRequestActPayload,
  PcmpRequestActResult,
  PcmpRequestDecision,
} from '../types/pcmpRequested.types';
import { pcmpActionCallbacks } from '../utils/pcmpActionCallbacks';

/** Process, approve or reject the picked requests in bulk. */
export function useActOnPcmpRequests(onSuccess: (result: PcmpRequestActResult) => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PcmpRequestActPayload) => actOnPcmpRequests(payload),
    ...pcmpActionCallbacks<PcmpRequestActResult>(queryClient, describePcmpActOutcome, onSuccess),
  });
}

const SINGLE_TOAST = {
  PROCESS: 'Request marked Processing',
  APPROVE: 'Request approved',
  REJECT: 'Request rejected',
} as const satisfies Record<PcmpRequestDecision, string>;

/** The details page's form: act on the one open request of a proposal. */
export function useActOnPcmpProposalRequest(
  proposalId: number,
  decision: PcmpRequestDecision,
  onSuccess: () => void,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (comment: string | undefined) =>
      actOnPcmpProposalRequest(proposalId, decision, comment),
    ...pcmpActionCallbacks<PcmpRequestActed>(queryClient, SINGLE_TOAST[decision], onSuccess),
  });
}
